"use client";

import { clsx } from "clsx";
import { CategoryDetailsV1 } from "@repo/apis/browse-category-v1";
import { Product } from "@repo/apis/browse-search-v2";
import { Link } from "@repo/next-utils/router";
import { Box } from "@repo/ui/box";
import * as Container from "@repo/ui/container";
import * as Flex from "@repo/ui/flex";
import * as Grid from "@repo/ui/grid";
import * as ProductTile from "@repo/ui/product-tile";
import { SaveForLater } from "@repo/ui/saved-items";
import { VisuallyHidden } from "@repo/ui/visually-hidden";
import { removeBrandPrefix } from "@repo/utils/remove-brand-prefix";
import { useAnalytics } from "@/components/analytics/initialise-analytics";
import { Filters } from "@/components/filters";
import { GridLayout } from "@/components/grid-layout";
import { useGridLayout } from "@/components/grid-layout/grid-layout-context";
import { ProductTileColoursBadge } from "@/components/product-tile-colours-badge/product-tile-colours-badge";
import * as Viewport from "@/components/viewport";
import { BRAND_FIT_INDICATORS, COLLAGE, CTA_REF_KEY } from "@/constants";
import {
  TYPOGRAPHY_HEADING4,
  TYPOGRAPHY_BODY2,
  TYPOGRAPHY_BODY3,
  TYPOGRAPHY_BODY3_SENTENCE,
} from "@/theme/topshop/typography/typography-constants";
import { EnrichedFacet } from "@/utils/facets/enrich-facets";
import { formatProductUrl } from "@/utils/format-url";
import classes from "./products.module.css";

export type ProductsType = {
  products: Product[];
  country: string;
  lang: string;
  keyStoreDataversion: string;
  store: string;
  currency: string;
  facets: EnrichedFacet[];
  itemCount: number;
  searchQuery?: string;
  categoryDetails?: CategoryDetailsV1;
};

export function Products({
  products,
  country,
  currency,
  searchQuery,
  facets,
  itemCount,
  categoryDetails,
}: ProductsType) {
  const { columns } = useGridLayout();
  const { pushToDataLayer } = useAnalytics();
  const ctaRef = searchQuery
    ? "successful search results"
    : categoryDetails?.categoryDetails.id;

  const handleSaveForLaterAnalytics = (params: { productId: number }) => {
    const product = products.find((p) => p.id === params.productId);
    if (!product) {
      return;
    }

    const item_name = removeBrandPrefix({
      brand: product.brandName,
      text: product.name,
      fitIndicators: BRAND_FIT_INDICATORS,
    });

    const priceLocal = product.price?.current?.value;
    if (priceLocal == null) {
      return;
    }

    const pricePounds = product.price?.xrp?.value ?? priceLocal;

    pushToDataLayer({
      add_to_wishlist: {
        ecommerce: {
          currency: "GBP",
          value: pricePounds,
          localCurrencyCode: currency,
          valueLocalCurrency: priceLocal,
          items: [
            {
              productId: `${params.productId}`,
              productCode: `${product.productCode}`,
              item_name,
              item_brand: product.brandName,
              price: pricePounds,
              priceLocalCurrency: priceLocal,
              quantity: 1,
              itemType: "Product",
            },
          ],
        },
      },
    });
  };
  return (
    <Container.Root size="medium">
      <div className={classes.controlsRow}>
        <div className={classes.filtersGroup}>
          <Filters facets={facets} title="Filter" />
          <span
            className={`${TYPOGRAPHY_BODY3_SENTENCE} ${classes.mobileItemCount}`}
          >
            {itemCount} items
          </span>
        </div>
        <div className={classes.verticalRule} />
        <GridLayout />
      </div>
      {[1, 2, 3].includes(columns) ? (
        <>
          <Viewport.Small>
            <Grid.Container
              columns={columns === 1 ? 2 : columns}
              rowGap={columns === 3 ? undefined : "md"}
            >
              {products.map((product, index) => {
                const isFullSize = columns === 1 && index % 7 === 0;

                const formattedUrl = formatProductUrl(
                  product.url,
                  ctaRef
                    ? {
                        [CTA_REF_KEY]: ctaRef,
                      }
                    : undefined,
                );

                const name = removeBrandPrefix({
                  brand: product.brandName,
                  text: product.name,
                  fitIndicators: BRAND_FIT_INDICATORS,
                });

                return (
                  <Grid.Item key={product.id} span={isFullSize ? 2 : 1}>
                    <ProductTile.Root asChild>
                      <Link href={formattedUrl}>
                        <Flex.Container column>
                          <ProductTile.ProductImage
                            imageUrl={product.imageUrl}
                            altText={name}
                            sourceSetProfile={COLLAGE}
                            srcSize={240}
                          />
                          {columns === 3 ? (
                            <VisuallyHidden>
                              <Box paddingX="md" asChild>
                                <Flex.Container gap="2xs" column>
                                  <ProductTile.Title
                                    className={TYPOGRAPHY_HEADING4}
                                  >
                                    {name}
                                  </ProductTile.Title>
                                  <Flex.Container
                                    justify="space-between"
                                    align="center"
                                  >
                                    <ProductTile.Price
                                      priceData={product.price}
                                      country={country}
                                      className={TYPOGRAPHY_BODY2}
                                      previousPriceCopy="Previous Price"
                                      currentPriceCopy="Current Price"
                                      currency={currency}
                                    />
                                    <ProductTileColoursBadge
                                      product={product}
                                    />
                                  </Flex.Container>
                                </Flex.Container>
                              </Box>
                            </VisuallyHidden>
                          ) : (
                            <>
                              <Box paddingX="md" paddingY="md" asChild>
                                <Flex.Container column>
                                  <Flex.Item grow={1}>
                                    <ProductTile.Title
                                      className={TYPOGRAPHY_HEADING4}
                                    >
                                      {name}
                                    </ProductTile.Title>
                                  </Flex.Item>
                                  <Flex.Item>
                                    <Flex.Container gap="xs" align="center">
                                      <ProductTile.Price
                                        priceData={product.price}
                                        country={country}
                                        className={TYPOGRAPHY_BODY2}
                                        previousPriceCopy="Previous Price"
                                        currentPriceCopy="Current Price"
                                        currency={currency}
                                      />
                                      <ProductTileColoursBadge
                                        product={product}
                                      />
                                    </Flex.Container>
                                  </Flex.Item>
                                </Flex.Container>
                              </Box>
                              <SaveForLater
                                productId={product.id}
                                colourWayId={product.colourWayId}
                                className={classes.saveForLaterMobile}
                                onAdded={handleSaveForLaterAnalytics}
                              />
                            </>
                          )}
                        </Flex.Container>
                      </Link>
                    </ProductTile.Root>
                  </Grid.Item>
                );
              })}
            </Grid.Container>
          </Viewport.Small>
          <Viewport.Large>
            <Grid.Container columns={columns} rowGap="2xs">
              {products.map((product) => {
                const formattedUrl = formatProductUrl(
                  product.url,
                  ctaRef
                    ? {
                        [CTA_REF_KEY]: ctaRef,
                      }
                    : undefined,
                );
                const name = removeBrandPrefix({
                  brand: product.brandName,
                  text: product.name,
                  fitIndicators: BRAND_FIT_INDICATORS,
                });

                return (
                  <ProductTile.Root key={product.id} asChild>
                    <Link href={formattedUrl}>
                      <Flex.Container column>
                        <ProductTile.ProductImage
                          imageUrl={product.imageUrl}
                          altText={name}
                          sourceSetProfile={COLLAGE}
                          srcSize={163}
                          className={classes.productImage}
                        />
                        <Box paddingX="md" paddingY="md" asChild>
                          <Flex.Container column>
                            <Flex.Container
                              justify="space-between"
                              align="center"
                            >
                              <Flex.Container column>
                                <ProductTile.Title
                                  className={TYPOGRAPHY_HEADING4}
                                >
                                  {name}
                                </ProductTile.Title>
                                <ProductTile.Price
                                  priceData={product.price}
                                  country={country}
                                  className={TYPOGRAPHY_BODY3}
                                  previousPriceCopy="Previous Price"
                                  currentPriceCopy="Current Price"
                                  currency={currency}
                                />
                              </Flex.Container>
                              <Flex.Container align="center">
                                <ProductTileColoursBadge product={product} />
                                <SaveForLater
                                  productId={product.id}
                                  colourWayId={product.colourWayId}
                                  onAdded={handleSaveForLaterAnalytics}
                                />
                              </Flex.Container>
                            </Flex.Container>
                          </Flex.Container>
                        </Box>
                      </Flex.Container>
                    </Link>
                  </ProductTile.Root>
                );
              })}
            </Grid.Container>
          </Viewport.Large>
        </>
      ) : (
        <Grid.Container columns={columns} className={classes.columnGrid}>
          {products.map((product) => {
            const formattedUrl = formatProductUrl(
              product.url,
              ctaRef
                ? {
                    [CTA_REF_KEY]: ctaRef,
                  }
                : undefined,
            );
            const name = removeBrandPrefix({
              brand: product.brandName,
              text: product.name,
              fitIndicators: BRAND_FIT_INDICATORS,
            });

            return (
              <Grid.Item key={product.id} className={classes.gridItem}>
                <ProductTile.Root asChild className={classes.productTileRoot}>
                  <Link href={formattedUrl}>
                    <Flex.Container
                      column
                      gap="xs"
                      className={classes.productTileInner}
                    >
                      <div className={classes.productImage}>
                        <ProductTile.ProductImage
                          imageUrl={product.imageUrl}
                          altText={name}
                          sourceSetProfile={COLLAGE}
                          srcSize={163}
                          className={classes.productImage}
                        />
                        <Box className={classes.overlay} paddingX="sm" asChild>
                          <Flex.Container
                            justify="center"
                            align="center"
                            column
                            gap="xs"
                          >
                            <ProductTile.Title
                              className={TYPOGRAPHY_HEADING4}
                              maxLines={3}
                            >
                              {name}
                            </ProductTile.Title>
                            <ProductTile.Price
                              priceData={product.price}
                              country={country}
                              className={clsx(
                                TYPOGRAPHY_BODY3,
                                classes.overlayProductPrice,
                              )}
                              previousPriceCopy="Previous Price"
                              currentPriceCopy="Current Price"
                              currency={currency}
                            />
                            <SaveForLater
                              productId={product.id}
                              colourWayId={product.colourWayId}
                              onAdded={handleSaveForLaterAnalytics}
                            />
                          </Flex.Container>
                        </Box>
                      </div>
                    </Flex.Container>
                  </Link>
                </ProductTile.Root>
              </Grid.Item>
            );
          })}
        </Grid.Container>
      )}
    </Container.Root>
  );
}
